// Home-screen Menopause Score card. Compact: title, 7-dot sparkline,
// semicircle gauge, today's number, delta vs yesterday, band pill,
// insight one-liner, "View detail →" CTA.
//
// Pure presentation — the parent fetches via useQuery + fetchMenoScore and
// passes the series. Empty state renders when today.score === null.

import React from "react";
import { impactBandFor, generateDailyInsight, MIN_LOGGED_FOR_INCLUSION, SCORE_V2_CHANGE_NOTE, SCORE_V2_RELEASE_DATE, SCORE_V2_NOTE_EXPIRES_DAYS, type DailyScore, type FlaggedSymptom } from "./menoScore";
import { getScoreLabel } from "./scoreLabel";

const T = "#FFC1CD", TL = "#FFE4EA", TD = "#4B1F4F";
const GR = "#6E4B71", GL = "#FAF7F5", GD = "#6E4B71";
const WH = "#ffffff";

export function MenopauseScoreCard({
  series,
  hrtStartDate,
  onOpenDetail,
  onLogToday,
  stage,
  flaggedSymptoms = [],
  wearableAdjustment = null,
}: {
  series: DailyScore[];
  hrtStartDate: Date | null;
  onOpenDetail: () => void;
  onLogToday: () => void;
  stage?: string | null;
  flaggedSymptoms?: FlaggedSymptom[];
  /** Release 2 TEST-USER preview only. Display-only adjustment derived
   *  from Morning Readiness. Parent is responsible for gating; when null
   *  the line does not render. Never used in computeDailyScore. */
  wearableAdjustment?: { adjustedScore: number; delta: number; readiness: number } | null;
}) {
  const scoreLabel = getScoreLabel(stage).toUpperCase();
  // Headline = TODAY's raw daily score. Incomplete-day guard: if today
  // hasn't logged at least MIN_LOGGED_FOR_INCLUSION symptoms, show the
  // incomplete state instead of a misleading single-symptom score. The
  // 7-day rolling average lives on the detail page, not here.
  const today = series[series.length - 1];
  const isIncomplete = !today || today.score == null || today.loggedCount < MIN_LOGGED_FOR_INCLUSION;
  const todayScore = isIncomplete ? null : (today!.score as number);
  const band = impactBandFor(todayScore);
  // Day-over-day delta vs. the most recent prior eligible day.
  let yesterdayScore: number | null = null;
  if (!isIncomplete) {
    for (let i = series.length - 2; i >= 0; i--) {
      const s = series[i];
      if (s.score != null && s.loggedCount >= MIN_LOGGED_FOR_INCLUSION) { yesterdayScore = s.score; break; }
    }
  }
  const delta = todayScore != null && yesterdayScore != null ? todayScore - yesterdayScore : null;
  // Insight = single source of the flag phrase (appended inside
  // generateDailyInsight). The band line below shows only the band label —
  // no duplicate flag append here.
  const insight = generateDailyInsight(series, hrtStartDate, flaggedSymptoms);


  // Empty / incomplete state.
  if (todayScore == null) {
    return (
      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: "18px 18px 16px", marginBottom: 14 }}>
        <ScoreV2ChangeNote />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ fontSize: 13, color: "#FF5C7A", fontWeight: 600, letterSpacing: "0.02em" }}>{scoreLabel} · TODAY</div>
        </div>
        <div style={{ fontSize: 15, color: GD, fontWeight: 500, marginBottom: 6, lineHeight: 1.4 }}>
          Today's check-in is incomplete — log at least {MIN_LOGGED_FOR_INCLUSION} symptoms to see today's score.
        </div>
        <div style={{ fontSize: 12, color: GR, lineHeight: 1.5 }}>
          A 0–100 number that summarizes how today is going across your tracked symptoms.
        </div>
        <div style={{ marginTop: 10, textAlign: "right" }}>
          <span onClick={onLogToday} style={{ fontSize: 12, color: TD, fontWeight: 600, cursor: "pointer" }}>Log today →</span>
        </div>
      </div>
    );
  }

  // 7-day sparkline (oldest → newest). Empty days drop out — line connects
  // across gaps but the dot is grey so the user sees they didn't log.
  const last7 = series.slice(-7);
  const w = 120, h = 30, pad = 4;
  const xs = last7.map((_, i) => pad + i * ((w - 2 * pad) / Math.max(1, last7.length - 1)));
  const ys = last7.map(s => s.score == null ? null : (h - pad) - ((s.score / 100) * (h - 2 * pad)));
  // Draw a path using only segments between consecutive scored points.
  let path = "";
  let started = false;
  for (let i = 0; i < last7.length; i++) {
    const y = ys[i];
    if (y == null) { started = false; continue; }
    path += (started ? " L" : "M") + xs[i].toFixed(1) + "," + y.toFixed(1);
    started = true;
  }

  // Semicircle gauge.
  const gR = 38, gCx = 50, gCy = 50;
  const gAngle = (todayScore / 100) * Math.PI;
  const gx = gCx - gR * Math.cos(gAngle);
  const gy = gCy - gR * Math.sin(gAngle);
  const largeArc = gAngle > Math.PI ? 1 : 0;
  const arcPath = `M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 ${largeArc} 1 ${gx.toFixed(1)} ${gy.toFixed(1)}`;

  return (
    <div onClick={onOpenDetail} style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: "16px 18px 14px", marginBottom: 14, cursor: "pointer" }}>
      <ScoreV2ChangeNote />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: "#FF5C7A", fontWeight: 600, letterSpacing: "0.08em" }}>{scoreLabel} · TODAY</div>
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
          <path d={path} fill="none" stroke={band.color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          {last7.map((s, i) => {
            const y = ys[i]; if (y == null) return null;
            const isToday = i === last7.length - 1;
            return <circle key={i} cx={xs[i]} cy={y} r={isToday ? 3 : 1.8} fill={isToday ? band.color : GR} />;
          })}
        </svg>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <svg width={100} height={62} viewBox="0 0 100 62" aria-label={`Score ${todayScore}`}>
          <path d={`M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 1 1 ${gCx + gR} ${gCy}`} fill="none" stroke={GL} strokeWidth={6} strokeLinecap="round" />
          <path d={arcPath} fill="none" stroke={band.color} strokeWidth={6} strokeLinecap="round" />
          <text x={gCx} y={gCy - 2} textAnchor="middle" fontSize="22" fontWeight="600" fill={GD}>{todayScore}</text>
          <text x={gCx} y={gCy + 12} textAnchor="middle" fontSize="9" fill={GR}>of 100</text>
          
        </svg>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: band.color + "22", color: band.color, fontSize: 11, fontWeight: 500 }}>Today's symptom load: {band.label} ({todayScore})</span>
          </div>
          {delta != null && (
            <div style={{ fontSize: 11, color: GR, marginBottom: 4 }}>
              {delta > 0 ? "↑" : delta < 0 ? "↓" : "→"} {Math.abs(delta)} vs. yesterday
            </div>
          )}
          <div style={{ fontSize: 10.5, color: GR, marginBottom: 4 }}>
            Based on {today!.loggedCount} symptom{today!.loggedCount === 1 ? "" : "s"} logged today
          </div>
          <div style={{ fontSize: 11.5, color: GD, lineHeight: 1.45 }}>{insight.text}</div>
          {wearableAdjustment && (
            <div style={{
              marginTop: 6, padding: "5px 8px", borderRadius: 8,
              background: "#FFE4EA", border: "0.5px dashed #FFB0BD",
              fontSize: 10.5, color: TD, lineHeight: 1.4,
            }}>
              <span style={{ fontWeight: 600 }}>Wearable-adjusted preview:</span>{" "}
              {wearableAdjustment.adjustedScore}
              {" "}
              ({wearableAdjustment.delta > 0 ? "+" : wearableAdjustment.delta < 0 ? "" : "±"}{wearableAdjustment.delta} from readiness {wearableAdjustment.readiness})
              <span style={{ display: "block", color: GR, fontWeight: 400, marginTop: 2 }}>
                Test preview · display only · official score unchanged
              </span>
            </div>
          )}

          <div style={{ fontSize: 12, color: TD, fontWeight: 700, marginTop: 4, lineHeight: 1.4 }}>Updates when you save today's check-in.</div>
        </div>
      </div>
      <div style={{ marginTop: 10, textAlign: "right" }}>
        <span style={{ fontSize: 11.5, color: TD, fontWeight: 500 }}>View detail →</span>
      </div>
    </div>
  );
}

// v2.0 change note — dismissible, auto-expires SCORE_V2_NOTE_EXPIRES_DAYS
// after the release date. localStorage key kept simple so a future v3
// note can reuse the same dismiss mechanism by bumping the constant.
export function ScoreV2ChangeNote() {
  const DISMISS_KEY = "menoscore_v2_note_dismissed";
  const [hidden, setHidden] = React.useState(true);
  React.useEffect(() => {
    try {
      const release = new Date(SCORE_V2_RELEASE_DATE + "T00:00:00");
      const expiresAt = new Date(release.getTime() + SCORE_V2_NOTE_EXPIRES_DAYS * 86400000);
      if (Date.now() >= expiresAt.getTime()) { setHidden(true); return; }
      if (window.localStorage.getItem(DISMISS_KEY) === "1") { setHidden(true); return; }
      setHidden(false);
    } catch { setHidden(true); }
  }, []);
  if (hidden) return null;
  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 10, background: "#FFF6F8", border: "0.5px solid #F1C7D1", color: "#4B1F4F", fontSize: 11.5, lineHeight: 1.5, display: "flex", alignItems: "flex-start", gap: 8 }}
    >
      <span style={{ flex: 1 }}>{SCORE_V2_CHANGE_NOTE}</span>
      <button
        aria-label="Dismiss score update note"
        onClick={() => { try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch {} setHidden(true); }}
        style={{ background: "none", border: "none", color: "#6E4B71", fontSize: 14, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}
      >×</button>
    </div>
  );
}

